import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TeamAvatar, ThemeToggle, LiveTag, fmtDateTime, ShareDialog } from "@/components/common";
import ResultDialog from "@/components/manage/ResultDialog";
import { TournamentResults, TournamentStandings } from "@/components/TournamentSummary";
import ImageUpload from "@/components/ImageUpload";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Pencil, Users, Megaphone, Lock, ClipboardEdit, Check, X, Share2 } from "lucide-react";

export default function CaptainView() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [nf, setNf] = useState(false);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editingTeamName, setEditingTeamName] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [active, setActive] = useState(null);
  const [shareMembers, setShareMembers] = useState(false);

  const load = useCallback(() => {
    api.get(`/access/captain/${code}`).then((r) => setD(r.data)).catch(() => setNf(true));
  }, [code]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  if (nf) return <Center>Invalid captain link.</Center>;
  if (!d) return <Center>Loading…</Center>;

  const team = d.team;
  const ctx = d.context;
  const t = ctx.tournament;
  const primary = "hsl(var(--primary))";
  const isDraft = t.status === "draft";
  const max = t.max_members;
  const teamMap = {};
  ctx.teams.forEach((tm) => { teamMap[tm.id] = tm; });
  const myMatches = ctx.matches.filter((m) => (m.team_a && m.team_a.id === team.id) || (m.team_b && m.team_b.id === team.id))
    .map((m) => ({ ...m, team_a_id: m.team_a?.id || null, team_b_id: m.team_b?.id || null }));

  const addMember = async () => {
    if (!name.trim()) return;
    try { await api.post(`/access/captain/${code}/members`, { name }); setName(""); toast.success("Member added"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveTeamName = async () => {
    if (!teamName.trim()) return;
    try {
      await api.put(`/access/captain/${code}/team`, { name: teamName });
      setEditingTeamName(false);
      toast.success("Team name updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveTeamLogo = async (logo_url) => {
    try {
      await api.put(`/access/captain/${code}/team/logo`, { logo_url });
      toast.success(logo_url ? "Team logo updated" : "Team logo removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveEdit = async (id) => { await api.put(`/access/captain/${code}/members/${id}`, { name: editName }); setEditing(null); load(); };
  const delMember = async (id) => { await api.delete(`/access/captain/${code}/members/${id}`); load(); };

  return (
    <div className="min-h-screen bg-background pb-10">
      <header className="flex items-center justify-between border-b p-4">
        <div className="flex items-center gap-3">
          <TeamAvatar team={team} size={40} />
          <div>
            <div className="flex items-center gap-1 font-heading font-bold leading-tight">
              {team.name}
              {d.allow_team_rename && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingTeamName(true); setTeamName(team.name); }} data-testid="edit-team-name"><Pencil className="h-3.5 w-3.5" /></Button>}
            </div>
            <div className="text-xs text-muted-foreground">Captain · {t.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setShareMembers(true)} aria-label="Share with team members" data-testid="share-team-members">
            <Share2 className="h-4 w-4" /><span className="hidden sm:inline">Share with members</span>
          </Button>
          <ThemeToggle />
        </div>
      </header>

      <div className="mx-auto max-w-md space-y-6 p-4">
        {d.allow_team_rename && editingTeamName && (
          <Card className="space-y-3 p-4">
            <h2 className="font-heading font-bold">Change team name</h2>
            <Input value={teamName} onChange={(e) => setTeamName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveTeamName()} data-testid="team-name-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveTeamName} data-testid="save-team-name"><Check className="mr-1 h-3.5 w-3.5" /> Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingTeamName(false)}><X className="mr-1 h-3.5 w-3.5" /> Cancel</Button>
            </div>
          </Card>
        )}

        {d.allow_logo_change && (
          <Card className="space-y-3 p-4">
            <h2 className="font-heading font-bold">Team logo</h2>
            <ImageUpload value={team.logo_url} onChange={saveTeamLogo} label="Upload team logo" testId="captain-team-logo" />
          </Card>
        )}

        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading font-bold"><Users className="h-4 w-4" /> Team members</h2>
            <span className="text-xs text-muted-foreground">{team.members.length}/{max}</span>
          </div>
          <div className="mb-3 flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMember()} placeholder="Add member name" data-testid="member-name-input" />
            <Button onClick={addMember} data-testid="add-member-button"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-1.5">
            {team.members.length === 0 && <p className="text-sm text-muted-foreground">No members yet.</p>}
            {team.members.map((m) => (
              <div key={m.id} className="flex items-center gap-2 rounded-lg border p-2" data-testid={`member-${m.id}`}>
                {editing === m.id ? (
                  <>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" />
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => saveEdit(m.id)}><Check className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(null)}><X className="h-4 w-4" /></Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">{m.name}</span>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(m.id); setEditName(m.name); }} data-testid={`edit-member-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => delMember(m.id)} data-testid={`delete-member-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </Card>

        {isDraft ? <DraftNotice /> : (
          <div>
            <h2 className="text-label mb-2 text-muted-foreground">Our matches</h2>
            {myMatches.length === 0 ? <Empty>No matches scheduled yet.</Empty> : (
              <div className="space-y-2">{myMatches.map((m) => {
                const isFinal = m.status === "final";
                const canEnter = d.allow_results && !isFinal && m.team_a_id && m.team_b_id;
                return (
                  <Card key={m.id} className={`p-3 ${canEnter ? "cursor-pointer" : ""}`} onClick={() => canEnter && setActive(m)} data-testid={`captain-match-${m.id}`}>
                    <div className="mb-1 flex justify-between text-xs text-muted-foreground"><span>{m.round_name} · {m.field || "—"}</span>{isFinal ? <span className="flex items-center gap-1 text-emerald-600"><Lock className="h-3 w-3" /> FINAL</span> : m.status === "live" ? <LiveTag /> : <span>{fmtDateTime(m.scheduled_time, m.timezone)}</span>}</div>
                    <div className="flex justify-between"><span>{m.team_a?.name || m.team_a_placeholder}</span><span className="font-bold">{m.score_a ?? ""}</span></div>
                    <div className="flex justify-between"><span>{m.team_b?.name || m.team_b_placeholder}</span><span className="font-bold">{m.score_b ?? ""}</span></div>
                    {canEnter && <div className="mt-2 flex items-center gap-1 text-xs font-medium" style={{ color: primary }}><ClipboardEdit className="h-3.5 w-3.5" /> Tap to enter result</div>}
                  </Card>
                );
              })}</div>
            )}
          </div>
        )}

        {!isDraft && <TournamentStandings context={ctx} teamId={team.id} />}

        {ctx.announcements?.length > 0 && (
          <div><h2 className="text-label mb-2 text-muted-foreground">Announcements</h2>
            {ctx.announcements.map((a) => <Card key={a.id} className="mb-2 flex gap-2 p-3"><Megaphone className="mt-0.5 h-4 w-4" style={{ color: primary }} /><div><div className="font-semibold">{a.title}</div><div className="text-sm text-muted-foreground">{a.body}</div></div></Card>)}
          </div>
        )}

        {!isDraft && <TournamentResults context={ctx} />}
      </div>

      <ResultDialog open={!!active} onOpenChange={() => setActive(null)} match={active} teamMap={teamMap} mode="captain" code={code} onDone={() => { load(); setActive(null); }} />
      <ShareDialog open={shareMembers} onOpenChange={setShareMembers} title="Team member access"
        subtitle="Share this QR code or link with team members for read-only access."
        url={`${window.location.origin}/m/${team.team_code}`} code={team.team_code} />
    </div>
  );
}

const Center = ({ children }) => <div className="flex h-screen items-center justify-center text-muted-foreground">{children}</div>;
const Empty = ({ children }) => <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">{children}</p>;
const DraftNotice = () => <Empty>This tournament is still in draft.</Empty>;
